// La fiche processus — le diagramme est la page, les étapes sont la marge.
//
// Left: the BPMN (always visible) with its toolbar and the roles matrix band.
// Right: the vertical rail of the 8 steps — an accordion carrying its own
// chronology (evidence lines on done steps, gate dots, returns in --err).
// A repo file without a cycle record renders the same layout with a light
// right panel (ux-design §3 "une mise en page, deux remplissages").
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeft, Check, ChevronDown, ChevronRight, Download, ExternalLink, GitBranch,
  Lock, Maximize2, Minus, Plus, RotateCcw, X, ZoomIn, ZoomOut,
} from 'lucide-react';
import { api } from '../api/client';
import { githubConnectUrl } from '../components/GithubAccount';
import { useRole } from '../context/RoleContext';
import { useUnsavedChangesGuard } from '../context/NavigationGuardContext';
import { useT } from '../i18n';
import { BareTextarea } from '../components/BareTextarea';
import { BpmnCanvas, type BpmnCanvasHandle } from '../components/BpmnCanvas';
import { ProcessWorkPanel } from '../components/ProcessWorkPanel';
import { ConfirmModal } from '../components/ConfirmModal';
import { FileTicketModal } from './TicketsPage';
import { formatDate, agoLabel, daysAgo, parseJSON } from '../utils/format';

const GATE_PHASES = ['validation_cde', 'validation_governance', 'final_validation'];

// ─────────────────────────────────────────────────────────────────────────────

export function ProcessPage({ processId, repoPath }: { processId: number | null; repoPath: string | null }) {
  if (!processId && repoPath) return <FileFiche path={repoPath} />;
  if (!processId) return <div className="page-loading"><div className="spinner" /></div>;
  return <CycleFiche processId={processId} />;
}

// ── Shared: the diagram column (toolbar + canvas + roles band) ───────────────

function DiagramColumn({ path, processId, lanes, roles, canEditDiagram, onRolesChanged, onCommitted, onDirtyChange, initialRef }: {
  path: string | null;
  initialRef?: string | null;
  processId?: number;
  lanes?: string[];
  roles?: any[];
  canEditDiagram: boolean;
  onRolesChanged?: () => void;
  onCommitted?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t, lang } = useT();
  const { isCde, isGovernance } = useRole();
  const canvasRef = useRef<BpmnCanvasHandle>(null);
  const [file, setFile] = useState<{ content: string; sha: string | null } | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [viewRef, setViewRef] = useState<string | null>(null);   // viewing an old revision
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // why the person cannot commit with their GitHub account right now (GitHub App mode)
  const [ghBlock, setGhBlock] = useState<{ code: string; login: string | null; repo: string; whileEditing: boolean } | null>(null);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [people, setPeople] = useState<any[]>([]);
  const [functions, setFunctions] = useState<any[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const loadFile = (ref?: string) => {
    if (!path) return;
    setError(null);
    api.getRepoFile(path, ref)
      .then(f => { setFile(f); setViewRef(ref ?? null); })
      .catch(e => setError(e.message));
  };
  useEffect(() => { setFile(null); loadFile(initialRef ?? undefined); if (path) api.getRepoHistory(path).then(setHistory).catch(() => {}); }, [path,initialRef]);
  useEffect(() => {
    if (rolesOpen && isCde) { api.getPeople().then(setPeople).catch(() => {}); api.getFunctions().then(setFunctions).catch(() => {}); }
  }, [rolesOpen, isCde]);

  // Before any edit: in GitHub App mode a commit needs the person's own
  // account with push rights. Checking first means no edit is ever lost on a
  // "link your account" round trip.
  const startEditing = async () => {
    setGhBlock(null);
    try {
      const me = await api.getGithubMe();
      if (me.write_mode === 'user' && !me.can_write) {
        const code = !me.linked ? 'github_not_linked' : me.identity?.status === 'expired' ? 'github_token_expired' : 'github_no_write';
        setGhBlock({ code, login: me.identity?.login ?? null, repo: me.repo.full_name, whileEditing: false });
        return;
      }
    } catch { /* the server decides at commit time anyway */ }
    setEditing(true);
  };

  const ghBlockFromCommit = (data: any) => {
    api.getGithubMe().catch(() => null).then(me => {
      setGhBlock({ code: data.code, login: data.login ?? me?.identity?.login ?? null, repo: me?.repo?.full_name ?? '', whileEditing: true });
    });
  };

  const recheckGithub = async () => {
    const identity = await api.recheckGithub().catch(() => null);
    if (identity?.can_write) {
      const wasEditing = ghBlock?.whileEditing;
      setGhBlock(null);
      if (!wasEditing) setEditing(true);
    }
  };

  const commit = async () => {
    if (!canvasRef.current || !path || !file) return;
    setSaving(true);
    setConflict(null);
    try {
      const xml = await canvasRef.current.saveXML();
      const out = await api.commitRepoFile({
        path, content: xml, sha: file.sha, process_id: processId,
        message: `${processId ? `cde${processId} — ` : ''}mise à jour du processus depuis CdE`,
      });
      setFile({ content: xml, sha: out.sha ?? file.sha });
      setDirty(false);
      onDirtyChange?.(false);
      setEditing(false);
      api.getRepoHistory(path).then(setHistory).catch(() => {});
      onCommitted?.();
    } catch (e: any) {
      // the person's GitHub account cannot sign this commit — say why, keep the edit
      if (e.status === 403 && typeof e.data?.code === 'string' && e.data.code.startsWith('github_')) {
        ghBlockFromCommit(e.data);
        return;
      }
      // a stale sha is a CONFLICT, not a failure: name the commit that beat us
      const head = history[0];
      if (e.status === 409) {
        setConflict(t('fiche.diagramConflict', { sha: head?.sha?.slice(0, 8) ?? '—', author: head?.author ?? '—' }));
      } else setError(t('fiche.diagramError'));
    } finally {
      setSaving(false);
    }
  };

  const highlight = (lane: string) => {
    const next = highlighted === lane ? null : lane;
    setHighlighted(next);
    canvasRef.current?.highlightByName(next);
  };

  const download = () => {
    if (!file || !path) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([file.content], { type: 'application/xml' }));
    a.download = path.split('/').pop()!;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const showRoles = (roles && roles.length > 0) || (lanes && lanes.length > 0);

  return (
    <div className={`bpmn-frame${fullscreen ? ' bpmn-frame--full' : ''}`}
         style={fullscreen ? { position: 'fixed', inset: 'var(--topbar-h, 48px) 0 0 0', zIndex: 40, background: 'var(--surface)' } : { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="bpmn-toolbar">
        <button className="btn-icon" title={t('fiche.zoomIn')} onClick={() => canvasRef.current?.zoom(0.2)}><ZoomIn className="icon-sm" /></button>
        <button className="btn-icon" title={t('fiche.zoomOut')} onClick={() => canvasRef.current?.zoom(-0.2)}><ZoomOut className="icon-sm" /></button>
        <button className="btn-icon" title={t('fiche.fit')} onClick={() => canvasRef.current?.fit()}><Minus className="icon-sm" /></button>
        <button className="btn-icon" title={t('fiche.fullscreen')} onClick={() => setFullscreen(f => !f)}><Maximize2 className="icon-sm" /></button>

        {history.length > 0 && (
          <span style={{ position: 'relative' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setVersionsOpen(o => !o)}>
              <RotateCcw className="icon-sm" /> {viewRef ? viewRef.slice(0, 8) : history[0]?.sha?.slice(0, 8)}
            </button>
            {versionsOpen && (
              <div className="profile-menu-panel" style={{ left: 0, right: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                {history.map((c, i) => (
                  <button key={c.sha} className={`profile-menu-item${(viewRef ?? history[0]?.sha) === c.sha ? ' profile-menu-item--active' : ''}`}
                          onClick={() => { setVersionsOpen(false); loadFile(i === 0 ? undefined : c.sha); }}>
                    <span className="mono">{c.sha.slice(0, 8)}</span>
                    <span className="text-ink-3">{formatDate(c.date, lang)} · {c.author}</span>
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
        {viewRef && (
          <button className="btn btn-sm" onClick={() => loadFile()}>{t('fiche.diagramReload')} (HEAD)</button>
        )}

        <span className="spacer" />
        <button className="btn-icon" title={t('fiche.download')} onClick={download}><Download className="icon-sm" /></button>
        {canEditDiagram && !viewRef && (
          editing ? (
            <>
              <span className="text-ink-3 text-xs">{dirty ? '●' : ''}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setDirty(false); onDirtyChange?.(false); loadFile(); }}>{t('common.cancel')}</button>
              <button className="btn btn-accent btn-sm" disabled={!dirty || saving} onClick={commit}>
                {saving ? t('common.loading') : t('common.save')}
              </button>
            </>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={startEditing}>{t('common.edit')}</button>
          )
        )}
      </div>

      {conflict && (
        <div className="bpmn-toolbar" style={{ color: 'var(--err)' }}>
          <span className="text-sm">{conflict}</span>
          <span className="spacer" />
          <button className="btn btn-sm" onClick={() => { setConflict(null); loadFile(); }}>{t('fiche.diagramReload')}</button>
        </div>
      )}
      {ghBlock && (
        <div className="gh-block">
          <GitBranch className="icon-sm" />
          <span>
            {t(ghBlock.code === 'github_not_linked' ? 'gh.block.notLinked' : ghBlock.code === 'github_token_expired' ? 'gh.block.expired' : 'gh.block.noWrite',
               { login: ghBlock.login ?? '—', repo: ghBlock.repo })}
          </span>
          <span className="spacer" />
          {ghBlock.code === 'github_no_write' ? (
            <button className="btn btn-sm" onClick={recheckGithub}>{t('gh.recheck')}</button>
          ) : (
            <a className="btn btn-accent btn-sm" href={githubConnectUrl()} target={ghBlock.whileEditing ? '_blank' : undefined} rel="noreferrer">
              {t('gh.link')}
            </a>
          )}
          {ghBlock.whileEditing && ghBlock.code !== 'github_no_write' && <span className="text-xs text-ink-3">{t('gh.block.newTab')}</span>}
          <button className="btn-icon" title={t('common.close')} onClick={() => setGhBlock(null)}><X className="icon-sm" /></button>
        </div>
      )}
      {error && !file && (
        <div className="bpmn-empty" style={{ height: 'auto', padding: 'var(--s24)' }}>
          {t('fiche.diagramError')} <button className="btn btn-sm" onClick={() => loadFile(viewRef ?? undefined)}>{t('common.retry')}</button>
        </div>
      )}

      {path === null ? (
        <div className="bpmn-canvas bpmn-canvas--fill"><div className="bpmn-empty">{t('fiche.diagramNone')}</div></div>
      ) : !file && !error ? (
        <div className="bpmn-canvas bpmn-canvas--fill"><div className="bpmn-empty">{t('fiche.diagramLoading')}</div></div>
      ) : file && (
        <BpmnCanvas
          ref={canvasRef}
          xml={file.content}
          editable={editing}
          fill
          emptyMessage={t('fiche.diagramEmpty')}
          onDirty={(d) => { setDirty(d); onDirtyChange?.(d); }}
        />
      )}

      {showRoles && !fullscreen && (
        <div className="roles-band">
          <div className="roles-band-head" onClick={() => setRolesOpen(o => !o)}>
            {rolesOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>{t('fiche.rolesInDiagram')}</span>
          </div>
          {!rolesOpen ? (
            <div className="roles-chips">
              {(roles ?? (lanes ?? []).map(l => ({ lane_name: l }))).map((r: any) => (
                <button key={r.lane_name} className={`role-chip${r.person_name ? '' : ' role-chip--unassigned'}`}
                        onClick={() => highlight(r.lane_name)}>
                  <span>{r.lane_name}</span>
                  <span className="role-chip-person">· {r.person_name ?? t('fiche.rolesUnassigned')}</span>
                </button>
              ))}
            </div>
          ) : roles ? (
            <table className="roles-table">
              <thead>
                <tr>
                  <th>{t('fiche.rolesInDiagram')}</th><th>{t('fiche.rolesPerson')}</th>
                  <th>{t('fiche.rolesFunction')}</th><th>{t('fiche.rolesCapacity')}</th><th>{t('fiche.rolesConfirmed')}</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r: any) => (
                  <tr key={r.id} className={r.stale ? 'stale' : undefined}>
                    <td>
                      <button className="role-chip" onClick={() => highlight(r.lane_name)}>{r.lane_name}</button>
                      {r.stale && <span className="text-xs text-ink-3"> {t('fiche.rolesStale')}</span>}
                    </td>
                    <td>
                      {isCde && processId ? (
                        <select className="form-select" style={{ width: 'auto', fontSize: 'var(--t-12)' }}
                                value={r.person_id ?? ''}
                                onChange={e => api.updateRole(processId, r.id, { person_id: Number(e.target.value) || null }).then(onRolesChanged)}>
                          <option value="">—</option>
                          {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (r.person_name ?? '—')}
                    </td>
                    <td>
                      {isCde && processId ? (
                        <select className="form-select" style={{ width: 'auto', fontSize: 'var(--t-12)' }}
                                value={r.function_id ?? ''}
                                onChange={e => api.updateRole(processId, r.id, { function_id: Number(e.target.value) || null }).then(onRolesChanged)}>
                          <option value="">—</option>
                          {functions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      ) : (r.function_name ?? '—')}
                    </td>
                    <td>
                      {isGovernance && processId ? (
                        <input className="form-input" style={{ width: 64, fontSize: 'var(--t-12)' }} placeholder="%"
                               defaultValue={r.capacity_pct ?? ''}
                               onBlur={e => api.updateRole(processId, r.id, { capacity_pct: Number(e.target.value) || null }).then(onRolesChanged)} />
                      ) : (r.capacity_pct != null ? `${r.capacity_pct} %` : '—')}
                    </td>
                    <td className="text-xs text-ink-3">
                      {r.confirmed_by_name
                        ? `${r.confirmed_by_name} · ${formatDate(r.confirmed_at, lang)}`
                        : isGovernance && processId
                          ? <button className="btn btn-sm" onClick={() => api.updateRole(processId, r.id, { confirm: true }).then(onRolesChanged)}>{t('common.confirm')}</button>
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="roles-chips">
              {(lanes ?? []).map(l => <span key={l} className="ghost-chip">{l}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── The full fiche for a cycle record ────────────────────────────────────────

function CycleFiche({ processId }: { processId: number }) {
  const { t, lang } = useT();
  const { isCde, isGovernance, isAdmin } = useRole();
  const [detail, setDetail] = useState<any | null>(null);
  const [phases, setPhases] = useState<any[]>([]);
  const [checkDefs, setCheckDefs] = useState<Record<string, any[]>>({});
  const [roles, setRoles] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [wide, setWide] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const load = () => Promise.all([
    api.getProcess(processId).then(d => { setDetail(d); setSelected(s => s ?? d.phase_key); }),
    api.getRoles(processId).then(setRoles).catch(() => setRoles([])),
  ]);
  useEffect(() => {
    setDetail(null); setSelected(null);
    load();
    api.getPhases().then((d: any) => { setPhases(d.phases); setCheckDefs(d.checks); });
  }, [processId]);

  // ── intake + handoff form (PMO idiom: one object, one save, deep-diff dirty)
  const [form, setForm] = useState<any | null>(null);
  const [savedForm, setSavedForm] = useState<any | null>(null);
  useEffect(() => {
    if (!detail) return;
    const f = {
      name: detail.name ?? '', description: detail.description ?? '', goal: detail.goal ?? '',
      client: detail.client ?? '',
      consultant_person_id: detail.consultant_person_id, process_owner_person_id: detail.process_owner_person_id,
      horizon_start: detail.horizon_start?.slice(0, 10) ?? '', horizon_end: detail.horizon_end?.slice(0, 10) ?? '',
      system_impact: detail.system_impact ?? 'none',
      validation_cde: parseJSON<any>(detail.validation_cde, {}),
      handoff: parseJSON<any>(detail.handoff, {}),
    };
    setForm(f); setSavedForm(f);
  }, [detail?.id, detail?.updated_at]);
  const [diagramDirty, setDiagramDirty] = useState(false);
  const [workDirty,setWorkDirty] = useState(false);
  const dirty = !!(isCde && form && savedForm && JSON.stringify(form) !== JSON.stringify(savedForm));
  useUnsavedChangesGuard(dirty || diagramDirty || workDirty);

  const save = async () => {
    if (!form) return;
    await api.updateProcess(processId, {
      ...form,
      horizon_start: form.horizon_start || null,
      horizon_end: form.horizon_end || null,
      validation_cde: form.validation_cde, handoff: form.handoff,
    });
    setBanner({ ok: true, text: t('common.save') + ' ✓' });
    setTimeout(() => setBanner(null), 2500);
    await load();
  };

  if (!detail || !form) return <div className="page-loading"><div className="spinner" /></div>;

  const currentPhase = phases.find(p => p.key === detail.phase_key);
  const currentOrder = currentPhase?.sort_order ?? 1;
  const nextPhase = phases.find(p => p.sort_order === currentOrder + 1);

  // gate state of the CURRENT phase
  const defs = checkDefs[detail.phase_key] ?? [];
  const missing = defs.filter(d => !detail.checks.some((c: any) => c.phase_key === detail.phase_key && c.check_key === d.key && c.checked));
  const gateDone = missing.length === 0;
  const mover = detail.workflow_mode === 'full' && detail.state === 'active' && detail.work_status === 'in_progress' && (detail.phase_key === 'validation_governance' ? isGovernance || isAdmin : isCde);

  // last return event still standing (no forward move since)
  const lastReturn = detail.events.find((e: any) => e.event_type === 'phase_returned');
  const lastForward = detail.events.find((e: any) => e.event_type === 'phase_changed');
  const activeReturn = lastReturn && (!lastForward || new Date(lastReturn.created_at) > new Date(lastForward.created_at)) ? lastReturn : null;

  const advance = async () => {
    if (!nextPhase) return;
    try {
      await api.movePhase(processId, nextPhase.key);
      setSelected(nextPhase.key);
      await load();
    } catch (e: any) {
      const missNames = e.data?.missing
        ? e.data.missing.map((k: string) => { const d = (checkDefs[detail.phase_key] ?? []).find((x: any) => x.key === k); return d ? (lang === 'fr' ? d.label_fr : d.label_en) : k; })
        : [];
      setBanner({ ok: false, text: missNames.length ? t('fiche.advanceBlocked', { missing: missNames.join(', ') }) : (e.message || t('fiche.diagramError')) });
    }
    setAdvanceOpen(false);
  };

  const seanceCount = detail.notes.length;
  const evidence = (phase: any): string | null => {
    if (phase.sort_order >= currentOrder) return null;
    switch (phase.key) {
      case 'intake': return t('fiche.evidence.opened', { date: formatDate(detail.created_at, lang) });
      case 'collaboration': return (seanceCount + detail.versions.length) > 0 ? t('fiche.evidence.seances', { seances: seanceCount, versions: detail.versions.length }) : null;
      default: {
        const check = detail.checks.filter((c: any) => c.phase_key === phase.key && c.checked_at).sort((a: any, b: any) => b.checked_at.localeCompare(a.checked_at))[0];
        return check ? t('fiche.evidence.validated', { date: formatDate(check.checked_at, lang) }) : null;
      }
    }
  };

  return (
    <div className="fiche-page">
      <div className="fiche-head cde-form-header" style={{ marginBottom: 0 }}>
        <div className="fiche-head-main">
          <button className="form-back" style={{ marginBottom: 'var(--s8)' }} onClick={() => { window.location.hash = '#/repository'; }}>
            <ArrowLeft className="icon-sm" /> {t('common.back')}
          </button>
          <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
            <span className="badge">{detail.registry_code || detail.process_code}</span>
            {isCde
              ? <input className="form-input--bare bare-title" style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-17)' }}
                       value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              : <span className="bare-title" style={{ fontSize: 'var(--t-17)', fontWeight: 600 }}>{detail.name}</span>}
            {detail.kind === 'rework' && detail.reworks_process_id && (
              <a className="badge badge-clickable" href={`#/process/${detail.reworks_process_id}`}>
                {t('fiche.reworkOf', { code: detail.registry_code || detail.process_code })}
              </a>
            )}
            {detail.in_inventory && <span className="badge badge-ok">{t('gov.tab.inventory')}</span>}
            <span className="state-chip state-chip--cycle">
              {detail.workflow_mode === 'revision' ? t(detail.state === 'live' ? 'control.published' : 'control.revision') : <>{t('rep.state.step', { n: currentOrder })} · {lang === 'fr' ? currentPhase?.label_fr : currentPhase?.label_en}</>}
            </span>
          </div>
          {detail.git_path && (
            <div className="rep-path" style={{ marginTop: 2 }}>
              {detail.git_path}
              <a href={`https://github.com/Mr-Moonsilver/eveil-global-processes/blob/main/${encodeURI(detail.git_path)}`}
                 target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}><ExternalLink size={10} className="icon-inline" /></a>
            </div>
          )}
          {activeReturn && (
            <div className="text-sm text-err" style={{ marginTop: 4 }}>
              ↩ {t('fiche.returnedBanner', {
                from: phases.find(p => p.id === activeReturn.from_phase_id)?.sort_order ?? '?',
                date: formatDate(activeReturn.created_at, lang),
                motif: activeReturn.details?.reason ?? '—',
              })}
            </div>
          )}
          {detail.state === 'on_hold' && <div className="text-sm text-warn" style={{ marginTop: 4 }}>{t('fiche.paused')}</div>}
          {detail.state === 'cancelled' && <div className="text-sm text-ink-3" style={{ marginTop: 4 }}>{t('fiche.cancelled')}</div>}
        </div>

        <div className="flex items-center gap-8" style={{ flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setTicketOpen(true)}>{t('rep.reportProblem')}</button>
          {dirty && <button className="btn btn-accent btn-sm" onClick={save}>{t('common.save')}</button>}
          {mover && nextPhase && (
            gateDone ? (
              <button className="btn btn-accent btn-sm" onClick={() => setAdvanceOpen(true)}>
                {t('fiche.advance', { n: nextPhase.sort_order })} <ChevronRight className="icon-sm" />
              </button>
            ) : (
              <span className="text-sm text-ink-3" title={t('fiche.advanceBlocked', { missing: missing.map(m => lang === 'fr' ? m.label_fr : m.label_en).join(', ') })}>
                <Lock className="icon-inline icon-sm" /> {t('fiche.advanceBlocked', { missing: missing.map(m => lang === 'fr' ? m.label_fr : m.label_en).join(', ') })}
              </span>
            )
          )}
          {mover && currentOrder > 1 && detail.phase_key !== 'complete' && (
            <button className="btn btn-ghost btn-sm" onClick={() => setReturnOpen(true)}>{t('fiche.return')}</button>
          )}
        </div>
      </div>

      {banner && (
        <div className={`text-sm ${banner.ok ? 'text-ok' : 'text-err'}`} style={{ marginBottom: 'var(--s8)' }}>{banner.text}</div>
      )}

      <div className={`fiche-layout${wide ? ' fiche-layout--wide' : ''}`}>
        <div className="fiche-left">
          <DiagramColumn
            path={detail.git_path}
            processId={processId}
            roles={roles}
            canEditDiagram={isCde && ['active','on_hold'].includes(detail.state)}
            initialRef={['live','superseded'].includes(detail.state) ? detail.publication_ref : undefined}
            onRolesChanged={() => api.getRoles(processId).then(setRoles)}
            onCommitted={load}
            onDirtyChange={setDiagramDirty}
          />
          {!detail.git_path && isCde && (
            <div className="decision" style={{ marginTop: 'var(--s8)' }}>
              <PickFile processId={processId} tier={detail.git_tier} area={detail.git_area} onLinked={load} />
            </div>
          )}
        </div>

        <div className="fiche-rail-col">
          <button className="btn-icon" style={{ float: 'right' }} title={wide ? t('fiche.narrowPanel') : t('fiche.widenPanel')}
                  onClick={() => setWide(w => !w)}>
            {wide ? <ChevronRight className="icon-sm" /> : <ArrowLeft className="icon-sm" />}
          </button>
          <ProcessWorkPanel detail={detail} reload={load} onDirtyChange={setWorkDirty}/>
          {detail.workflow_mode === 'full' && phases.map(phase => (
            <RailStep
              key={phase.key}
              phase={phase} detail={detail} form={form} setForm={setForm}
              currentOrder={currentOrder} selected={selected} setSelected={setSelected}
              checkDefs={checkDefs} evidence={evidence(phase)} roles={roles}
              activeReturn={activeReturn} reload={load} save={save}
            />
          ))}
        </div>
      </div>

      <ConfirmModal
        open={advanceOpen}
        title={t('fiche.advance', { n: nextPhase?.sort_order ?? '' })}
        message={`${detail.name} → ${lang === 'fr' ? nextPhase?.label_fr : nextPhase?.label_en}`}
        confirmLabel={t('common.confirm')}
        onConfirm={advance}
        onCancel={() => setAdvanceOpen(false)}
      />
      {returnOpen && (
        <ReturnModal
          phases={phases.filter(p => p.sort_order < currentOrder)}
          onClose={() => setReturnOpen(false)}
          onReturn={async (phaseKey, motif) => {
            try {
              await api.movePhase(processId, phaseKey, motif);
              setReturnOpen(false);
              setSelected(phaseKey);
              await load();
            } catch (e: any) { setBanner({ ok: false, text: e.message }); }
          }}
        />
      )}
      {ticketOpen && (
        <FileTicketModal
          presetProcessId={processId}
          onClose={() => setTicketOpen(false)}
          onFiled={() => { setTicketOpen(false); setBanner({ ok: true, text: t('rep.reportProblem') + ' ✓' }); setTimeout(() => setBanner(null), 2500); }}
        />
      )}
    </div>
  );
}

// ── One step of the vertical rail ────────────────────────────────────────────

function RailStep({ phase, detail, form, setForm, currentOrder, selected, setSelected, checkDefs, evidence, roles, activeReturn, reload, save }: any) {
  const { t, lang } = useT();
  const isCurrent = phase.key === detail.phase_key;
  const isDone = phase.sort_order < currentOrder;
  const isFuture = phase.sort_order > currentOrder;
  const isOpen = selected === phase.key;
  const isGate = GATE_PHASES.includes(phase.key);
  const returnedHere = activeReturn && isCurrent;

  const defs = checkDefs[phase.key] ?? [];
  const done = defs.filter((d: any) => detail.checks.some((c: any) => c.phase_key === phase.key && c.check_key === d.key && c.checked)).length;

  const cls = ['rail-step',
    isDone && 'rail-step--done', isCurrent && 'rail-step--current',
    isFuture && 'rail-step--future', returnedHere && 'rail-step--returned',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <button className="rail-step-head" onClick={() => setSelected(isOpen ? null : phase.key)}>
        <span className="rail-num">{isDone ? <Check size={11} /> : returnedHere ? '↩' : phase.sort_order}</span>
        <span className="rail-label">{lang === 'fr' ? phase.label_fr : phase.label_en}</span>
        <span className="rail-side">
          {isGate && !isDone && (
            <span className="gate-dots">
              {defs.map((d: any, i: number) => <span key={d.key} className={`gate-dot${i < done ? ' on' : ''}`} />)}
            </span>
          )}
          {isOpen ? <ChevronDown size={13} className="icon-muted" /> : <ChevronRight size={13} className="icon-muted" />}
        </span>
      </button>
      {!isOpen && evidence && <div className="rail-evidence">{evidence}</div>}
      {!isOpen && returnedHere && (
        <div className="rail-evidence rail-evidence--returned">
          {t('fiche.returnedBanner', {
            from: '', date: formatDate(activeReturn.created_at, lang), motif: activeReturn.details?.reason ?? '—',
          }).replace(/^.*?—/, '↩ —')}
        </div>
      )}
      {isOpen && (
        <div className="rail-panel">
          {isFuture
            ? <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('fiche.stageNotStarted')}</p>
            : <StepPanel phase={phase} detail={detail} form={form} setForm={setForm} roles={roles} reload={reload} save={save} defs={defs} />}
        </div>
      )}
    </div>
  );
}

// ── Per-step panels ──────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group" style={{ marginBottom: 'var(--s12)' }}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function ReadOr({ can, value, children }: { can: boolean; value: ReactNode; children: ReactNode }) {
  return can ? <>{children}</> : <div className="form-input--bare" style={{ cursor: 'default' }}>{value || '—'}</div>;
}

function StepPanel({ phase, detail, form, setForm, roles, reload, save, defs }: any) {
  switch (phase.key) {
    case 'intake': return <IntakePanel detail={detail} form={form} setForm={setForm} />;
    case 'collaboration': return <SeancesPanel detail={detail} reload={reload} />;
    case 'validation_cde': return <GatePanel detail={detail} phase={phase} defs={defs} owner="cde" reload={reload} form={form} setForm={setForm} save={save} />;
    case 'kpi_definition': return <KpiPanel detail={detail} reload={reload} />;
    case 'validation_governance': return <GatePanel detail={detail} phase={phase} defs={defs} owner="governance" reload={reload} roles={roles} />;
    case 'final_validation': return <GatePanel detail={detail} phase={phase} defs={defs} owner="cde" reload={reload} recap roles={roles} />;
    case 'systems': return <HandoffPanel detail={detail} form={form} setForm={setForm} save={save} />;
    case 'complete': return <LivePanel detail={detail} />;
    default: return null;
  }
}

function IntakePanel({ detail, form, setForm }: any) {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const [people, setPeople] = useState<any[]>([]);
  useEffect(() => { api.getPeople().then(setPeople).catch(() => {}); }, []);
  const personName = (id: number | null) => people.find(p => p.id === id)?.name ?? '—';

  return (
    <div>
      <div className="detail-row" style={{ marginBottom: 'var(--s12)' }}>
        <span className="detail-row-label">{t('fiche.p1.origin')}</span>
        <span className="text-sm">
          {detail.pmo_project_id
            ? t('fiche.p1.originPmo', { project: detail.pmo_project_name ?? detail.pmo_project_id, deliverable: detail.pmo_deliverable_id ?? '—' })
            : detail.description?.startsWith('Ouvert depuis le ticket') || /ticket #\d+/.test(detail.description ?? '')
              ? (detail.description ?? '').split('\n')[0]
              : t('fiche.p1.originCde')}
        </span>
      </div>
      <Field label={t('fiche.p1.description')}>
        <ReadOr can={isCde} value={detail.description}>
          <BareTextarea value={form.description} onChange={v => setForm({ ...form, description: v })} />
        </ReadOr>
      </Field>
      <Field label={t('fiche.p1.goal')}>
        <ReadOr can={isCde} value={detail.goal}>
          <BareTextarea value={form.goal} onChange={v => setForm({ ...form, goal: v })} />
        </ReadOr>
      </Field>
      <Field label={t('fiche.p1.client')}>
        <ReadOr can={isCde} value={detail.client}>
          <input className="form-input--bare" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} />
        </ReadOr>
      </Field>
      <div className="flex gap-12">
        <Field label={t('fiche.p1.consultant')}>
          <ReadOr can={isCde} value={detail.consultant_name}>
            <select className="form-select" value={form.consultant_person_id ?? ''}
                    onChange={e => setForm({ ...form, consultant_person_id: Number(e.target.value) || null })}>
              <option value="">—</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </ReadOr>
          {detail.consultant_name?.includes(' / ') && <p className="text-xs text-ink-3">{t('rep.sharedAssignment',{names:detail.consultant_name})}</p>}
        </Field>
        <Field label={t('fiche.p1.owner')}>
          <ReadOr can={isCde} value={detail.process_owner_name ?? personName(detail.process_owner_person_id)}>
            <select className="form-select" value={form.process_owner_person_id ?? ''}
                    onChange={e => setForm({ ...form, process_owner_person_id: Number(e.target.value) || null })}>
              <option value="">—</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </ReadOr>
        </Field>
      </div>
      <div className="flex gap-12">
        <Field label={t('fiche.p1.horizonStart')}>
          <ReadOr can={isCde} value={formatDate(detail.horizon_start, lang)}>
            <input type="date" className="form-input" value={form.horizon_start}
                   onChange={e => setForm({ ...form, horizon_start: e.target.value })} />
          </ReadOr>
        </Field>
        <Field label={t('fiche.p1.horizonEnd')}>
          <ReadOr can={isCde} value={formatDate(detail.horizon_end, lang)}>
            <input type="date" className="form-input" value={form.horizon_end}
                   onChange={e => setForm({ ...form, horizon_end: e.target.value })} />
          </ReadOr>
        </Field>
      </div>
      <Field label={t('fiche.p1.systemImpact')}>
        {isCde ? (
          <div className="selectable-group">
            {['none', 'new_system', 'adapt_system'].map(k => (
              <button key={k} type="button" className={`selectable${form.system_impact === k ? ' active' : ''}`}
                      onClick={() => setForm({ ...form, system_impact: k })}>
                {t(`fiche.p1.impact.${k}`)}
              </button>
            ))}
          </div>
        ) : <div className="form-input--bare" style={{ cursor: 'default' }}>{t(`fiche.p1.impact.${detail.system_impact ?? 'none'}`)}</div>}
      </Field>
      <div className="detail-row">
        <span className="detail-row-label">{t('fiche.p1.location')}</span>
        <span className="text-sm">{detail.git_tier ?? '—'}{detail.git_area ? ` / ${detail.git_area}` : ''}</span>
      </div>
    </div>
  );
}

function SeancesPanel({ detail, reload }: any) {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const [adding, setAdding] = useState(false);
  const [openTranscripts, setOpenTranscripts] = useState<Set<number>>(new Set());
  const [draft, setDraft] = useState({ seance_date: new Date().toISOString().slice(0, 10), seance_kind: 'kickoff', participants: '', body: '' });

  const submit = async () => {
    await api.addSeance(detail.id, {
      ...draft,
      participants: draft.participants.split(',').map(s => s.trim()).filter(Boolean).map(name => ({ name })),
    });
    setDraft({ seance_date: new Date().toISOString().slice(0, 10), seance_kind: 'kickoff', participants: '', body: '' });
    setAdding(false);
    reload();
  };

  return (
    <div>
      {isCde && (
        <button className="btn btn-ghost btn-sm" style={{ marginBottom: 'var(--s8)' }} onClick={() => setAdding(a => !a)}>
          <Plus className="icon-sm" /> {t('fiche.p2.addSeance')}
        </button>
      )}
      {adding && (
        <div className="seance-card">
          <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
            <input type="date" className="form-input" value={draft.seance_date}
                   onChange={e => setDraft({ ...draft, seance_date: e.target.value })} />
            <div className="selectable-group">
              {['kickoff', 'presentation', 'kpi', 'note'].map(k => (
                <button key={k} type="button" className={`selectable${draft.seance_kind === k ? ' active' : ''}`}
                        onClick={() => setDraft({ ...draft, seance_kind: k })}>{t(`fiche.p2.kind.${k}`)}</button>
              ))}
            </div>
          </div>
          <Field label={t('fiche.p2.participants')}>
            <input className="form-input--bare" placeholder="F. Dubois, S. Meyer…" value={draft.participants}
                   onChange={e => setDraft({ ...draft, participants: e.target.value })} />
          </Field>
          <Field label={t('fiche.p2.notes')}>
            <BareTextarea value={draft.body} onChange={v => setDraft({ ...draft, body: v })} />
          </Field>
          <div className="flex gap-8 justify-end">
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>{t('common.cancel')}</button>
            <button className="btn btn-accent btn-sm" onClick={submit}>{t('common.save')}</button>
          </div>
        </div>
      )}

      {detail.notes.length === 0 && !adding && (
        <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('fiche.p2.empty')}</p>
      )}
      {detail.notes.map((n: any) => {
        const participants = parseJSON<any[]>(n.participants, []);
        const transcriptOpen = openTranscripts.has(n.id);
        return (
          <div key={n.id} className="seance-card">
            <div className="seance-head">
              <span className="seance-date">{formatDate(n.seance_date ?? n.created_at, lang)}</span>
              {n.seance_kind && <span className="seance-kind">{t(`fiche.p2.kind.${n.seance_kind}`)}</span>}
              {n.kind === 'audio' && <span className="badge badge-plain">🎙</span>}
              {n.transcript_status === 'pending' && <span className="text-xs text-ink-3">{t('fiche.p2.transcriptPending')}</span>}
              {n.transcript_status === 'failed' && <span className="text-xs text-err">{t('fiche.p2.transcriptFailed')}</span>}
            </div>
            {participants.length > 0 && (
              <div className="seance-participants">{participants.map(p => p.name).join(' · ')}</div>
            )}
            {n.body && <div className="seance-body">{n.body}</div>}
            {n.transcript && (
              <>
                <button className="seance-fold" onClick={() => setOpenTranscripts(s => {
                  const next = new Set(s); next.has(n.id) ? next.delete(n.id) : next.add(n.id); return next;
                })}>
                  {transcriptOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {t('fiche.p2.transcript')} · {t('fiche.p2.transcriptWords', { n: n.transcript.split(/\s+/).length })}
                </button>
                {transcriptOpen && <div className="seance-transcript">{n.transcript}</div>}
              </>
            )}
            {n.commit_sha && (
              <div className="text-xs text-ink-3" style={{ marginTop: 'var(--s4)' }}>
                {t('fiche.p2.linkedCommit')} · <span className="mono">{n.commit_sha.slice(0, 8)}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GatePanel({ detail, phase, defs, owner, reload, form, setForm, save, recap, roles }: any) {
  const { t, lang } = useT();
  const { isCde, isGovernance, isAdmin } = useRole();
  const mayTick = owner === 'governance' ? (isGovernance || isAdmin) : isCde;
  const isCurrent = detail.phase_key === phase.key;
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [people, setPeople] = useState<any[]>([]);
  useEffect(() => { if (phase.key === 'validation_cde' && isCde) api.getPeople().then(setPeople).catch(() => {}); }, [phase.key, isCde]);

  const waitingDays = isCurrent && phase.key === 'validation_governance'
    ? daysAgo(detail.events.find((e: any) => e.event_type === 'phase_changed')?.created_at ?? detail.updated_at)
    : null;

  const toggle = async (checkKey: string, checked: boolean) => {
    await api.setCheck(detail.id, { phase_key: phase.key, check_key: checkKey, checked });
    reload();
  };

  const assigned = (roles ?? []).filter((r: any) => !r.stale && r.person_id).length;
  const totalRoles = (roles ?? []).filter((r: any) => !r.stale).length;

  return (
    <div>
      <div className="gate-owner">{owner === 'governance' ? t('fiche.gateOwnerGov') : t('fiche.gateOwnerCde')}</div>

      {recap && (
        <div className="detail-section" style={{ paddingBottom: 'var(--s12)', marginBottom: 'var(--s12)' }}>
          <div className="detail-label">{t('fiche.p6.recap')}</div>
          <ul className="detail-list text-sm">
            <li>{t('fiche.p6.recapRoles', { assigned, total: totalRoles })}</li>
            <li>{t('fiche.p6.recapKpis', { n: detail.kpis.length })}</li>
            <li>{t('fiche.p6.recapDashboards', { n: detail.dashboards.length })}</li>
            <li>{t('fiche.p6.recapSeances', { n: detail.notes.length })}</li>
          </ul>
        </div>
      )}

      {phase.key === 'validation_governance' && isCurrent && !isGovernance && !isAdmin && (
        <p className={`text-sm ${waitingDays != null && waitingDays > 14 ? 'text-warn' : 'text-ink-3'}`}>
          {t('fiche.p5.waiting', { n: waitingDays ?? 0 })}
        </p>
      )}

      {defs.map((d: any) => {
        const state = detail.checks.find((c: any) => c.phase_key === phase.key && c.check_key === d.key);
        const checked = !!state?.checked;
        // the governance box seen by a cde keeps a padlock (absence would mislead);
        // for anyone else who can't tick, the box is a read-only ✓/○ glyph, not a
        // dead grey checkbox.
        const showPadlock = owner === 'governance' && isCde;
        return (
          <div key={d.key} className="gate-check">
            <label className="gate-check-row" style={{ cursor: mayTick && isCurrent ? 'pointer' : 'default' }}>
              {mayTick
                ? <input type="checkbox" checked={checked} disabled={!isCurrent} onChange={e => toggle(d.key, e.target.checked)} />
                : <span className="gate-glyph" aria-hidden>{checked ? '✓' : '○'}</span>}
              <span>
                <span className="gate-check-label">{lang === 'fr' ? d.label_fr : d.label_en}</span>
                {showPadlock && (
                  <span className="gate-check-meta"><Lock size={10} className="icon-inline" /> {t('fiche.reservedGovernance')}</span>
                )}
                {state?.checked_at && (
                  <div className="gate-check-meta">{t('fiche.checkedBy', { date: formatDate(state.checked_at, lang), name: state.checked_by_name ?? '' })}</div>
                )}
                {d.key === 'inventory' && <div className="gate-check-meta">{t('fiche.p5.inventoryNote')}</div>}
              </span>
            </label>
          </div>
        );
      })}

      {phase.key === 'validation_cde' && (
        <Field label={t('fiche.p3.validatedWith')}>
          {isCde ? (
            <select className="form-select" value={form?.validation_cde?.validated_with_person_id ?? ''}
                    onChange={e => { setForm({ ...form, validation_cde: { ...form.validation_cde, validated_with_person_id: Number(e.target.value) || null } }); }}
                    >
              <option value="">—</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          ) : <div className="form-input--bare" style={{ cursor: 'default' }}>{'—'}</div>}
          <div className="text-xs text-ink-3" style={{ marginTop: 2 }}>{t('fiche.p3.validatedWithHint')}</div>
        </Field>
      )}

      {isCurrent && mayTick && phase.key !== 'final_validation' && (
        <div className="decision">
          <p className="decision-help">{phase.key === 'validation_cde' ? t('fiche.p3.needAdjust') : ''}</p>
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSendBackOpen(true)}>
              {phase.key === 'validation_governance' ? t('fiche.p5.sendBack') : t('fiche.p3.sendBack')}
            </button>
          </div>
        </div>
      )}

      {sendBackOpen && (
        <ReturnModal
          phases={[{ key: phase.key === 'validation_governance' ? 'kpi_definition' : 'collaboration', sort_order: phase.key === 'validation_governance' ? 4 : 2, label_fr: phase.key === 'validation_governance' ? 'KPIs & dashboards' : 'Collaboration client', label_en: '' }]}
          onClose={() => setSendBackOpen(false)}
          onReturn={async (phaseKey, motif) => {
            await api.movePhase(detail.id, phaseKey, motif);
            setSendBackOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function KpiPanel({ detail, reload }: any) {
  const { t } = useT();
  const { isCde, canSeeKpis } = useRole();
  const [shared, setShared] = useState<any[]>([]);
  const [adding, setAdding] = useState<'kpi' | 'dash' | null>(null);
  const [draft, setDraft] = useState({ name: '', definition: '', unit: '', target: '', source_system: '', is_shared: false, url: '' });
  const [inherited, setInherited] = useState(false);

  useEffect(() => { if (canSeeKpis) api.getKpis({ shared: '1' }).then(setShared).catch(() => {}); }, [canSeeKpis]);

  // a plain user sees the KPI NAMES + definitions (the server nulls the figures)
  // so they know a process is measured, and on what — but can't edit or add.
  if (!canSeeKpis) {
    return (
      <div>
        <div className="callout">{t('fiche.p4.figuresReserved')}</div>
        <div className="detail-label">{t('fiche.p4.kpis')}</div>
        {detail.kpis.length === 0
          ? <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('fiche.p4.noKpis')}</p>
          : detail.kpis.map((k: any) => (
              <div key={k.id} className="detail-row">
                <span className="detail-row-label" style={{ minWidth: 140 }}>{k.name}</span>
                <span className="text-sm">{k.definition ?? ''}</span>
              </div>
            ))}
      </div>
    );
  }

  const addKpi = async () => {
    if (!draft.name.trim()) return;
    await api.createKpi({ ...draft, process_id: detail.id });
    setDraft({ name: '', definition: '', unit: '', target: '', source_system: '', is_shared: false, url: '' });
    setAdding(null);
    reload();
  };
  const addDash = async () => {
    if (!draft.name.trim()) return;
    await api.createDashboard({ name: draft.name, definition: draft.definition, url: draft.url, is_shared: draft.is_shared, process_id: detail.id });
    setDraft({ name: '', definition: '', unit: '', target: '', source_system: '', is_shared: false, url: '' });
    setAdding(null);
    reload();
  };
  const inherit = async () => {
    const src = await api.getProcess(detail.reworks_process_id);
    for (const k of src.kpis) await api.linkKpi(k.id, detail.id).catch(() => {});
    setInherited(true);
    reload();
  };

  const availableShared = shared.filter(k => !detail.kpis.some((x: any) => x.id === k.id));

  return (
    <div>
      {detail.kind === 'rework' && detail.reworks_process_id && !inherited && isCde && (
        <button className="btn btn-ghost btn-sm" style={{ marginBottom: 'var(--s8)' }} onClick={inherit}>
          {t('fiche.p4.inherit', { code: detail.registry_code || detail.process_code })}
        </button>
      )}

      <div className="detail-label">{t('fiche.p4.kpis')}</div>
      {detail.kpis.length === 0
        ? <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('fiche.p4.noKpis')}</p>
        : detail.kpis.map((k: any) => (
            <div key={k.id} className="detail-row">
              <span className="detail-row-label" style={{ minWidth: 140 }}>{k.name}</span>
              <span className="text-sm">{k.definition ?? ''} {k.target ? `· ${t('obs.target', { t: k.target })}${k.unit ? ` ${k.unit}` : ''}` : ''}
                {k.is_shared && <span className="badge badge-plain" style={{ marginLeft: 6 }}>{t('fiche.p4.shared')}</span>}
              </span>
            </div>
          ))}

      {isCde && (
        <div className="flex gap-8" style={{ margin: 'var(--s8) 0' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setAdding(adding === 'kpi' ? null : 'kpi')}>
            <Plus className="icon-sm" /> {t('fiche.p4.addKpi')}
          </button>
          {availableShared.length > 0 && (
            <select className="form-select" style={{ width: 'auto', fontSize: 'var(--t-12)' }} value=""
                    onChange={e => { const id = Number(e.target.value); if (id) api.linkKpi(id, detail.id).then(reload); }}>
              <option value="">{t('fiche.p4.reuseShared')}</option>
              {availableShared.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          )}
        </div>
      )}
      {adding === 'kpi' && (
        <div className="seance-card">
          <Field label={t('fiche.p4.kpiName')}><input className="form-input--bare" autoFocus value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
          <Field label={t('fiche.p4.kpiDef')}><BareTextarea value={draft.definition} onChange={v => setDraft({ ...draft, definition: v })} /></Field>
          <div className="flex gap-8">
            <input className="form-input" placeholder={t('fiche.p4.kpiUnit')} value={draft.unit} onChange={e => setDraft({ ...draft, unit: e.target.value })} />
            <input className="form-input" placeholder={t('fiche.p4.kpiTarget')} value={draft.target} onChange={e => setDraft({ ...draft, target: e.target.value })} />
            <input className="form-input" placeholder={t('fiche.p4.kpiSource')} value={draft.source_system} onChange={e => setDraft({ ...draft, source_system: e.target.value })} />
          </div>
          <label className="gate-check-row" style={{ margin: 'var(--s8) 0' }}>
            <input type="checkbox" checked={draft.is_shared} onChange={e => setDraft({ ...draft, is_shared: e.target.checked })} />
            <span className="gate-check-label">{t('fiche.p4.shared')}</span>
          </label>
          <button className="btn btn-accent btn-sm" onClick={addKpi}>{t('common.add')}</button>
        </div>
      )}

      <div className="detail-label" style={{ marginTop: 'var(--s16)' }}>{t('fiche.p4.dashboards')}</div>
      {detail.dashboards.length === 0
        ? <p className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('fiche.p4.noDashboards')}</p>
        : detail.dashboards.map((d: any) => (
            <div key={d.id} className="detail-row">
              <span className="detail-row-label" style={{ minWidth: 140 }}>{d.name}</span>
              {d.url && <a className="text-accent text-sm" href={d.url} target="_blank" rel="noreferrer">{d.url}</a>}
            </div>
          ))}
      {isCde && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--s4)' }} onClick={() => setAdding(adding === 'dash' ? null : 'dash')}>
          <Plus className="icon-sm" /> {t('fiche.p4.addDashboard')}
        </button>
      )}
      {adding === 'dash' && (
        <div className="seance-card">
          <Field label={t('fiche.p4.dashName')}><input className="form-input--bare" autoFocus value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
          <Field label={t('fiche.p4.dashUrl')}><input className="form-input--bare" value={draft.url} onChange={e => setDraft({ ...draft, url: e.target.value })} /></Field>
          <button className="btn btn-accent btn-sm" onClick={addDash}>{t('common.add')}</button>
        </div>
      )}

      {/* preview of the NEXT gate: don't summon governance on an incomplete file */}
      <div className="decision">
        <p className="decision-help">
          {t('fiche.p4.gatePreview')} {t('gov.check.roles')} · {t('gov.check.kpis')} {detail.kpis.length > 0 ? '✓' : '✗'} ·{' '}
          {t('gov.check.dashboards')} {detail.dashboards.length > 0 ? '✓' : '✗'}
        </p>
      </div>
    </div>
  );
}

function HandoffPanel({ detail, form, setForm, save }: any) {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const h = form.handoff ?? {};
  const set = (patch: any) => setForm({ ...form, handoff: { ...h, ...patch } });

  return (
    <div>
      <p className="text-sm text-ink-3" style={{ fontStyle: 'italic', marginBottom: 'var(--s12)' }}>{t('fiche.p7.honest')}</p>
      <Field label={t('fiche.p7.target')}>
        <ReadOr can={isCde} value={h.target_system}>
          <input className="form-input--bare" value={h.target_system ?? ''} onChange={e => set({ target_system: e.target.value })} />
        </ReadOr>
      </Field>
      <Field label={t('fiche.p7.contact')}>
        <ReadOr can={isCde} value={h.contact}>
          <input className="form-input--bare" value={h.contact ?? ''} onChange={e => set({ contact: e.target.value })} />
        </ReadOr>
      </Field>
      <div className="flex gap-12">
        <Field label={t('fiche.p7.date')}>
          <ReadOr can={isCde} value={formatDate(h.date, lang)}>
            <input type="date" className="form-input" value={h.date ?? ''} onChange={e => set({ date: e.target.value })} />
          </ReadOr>
        </Field>
        <Field label={t('fiche.p7.link')}>
          <ReadOr can={isCde} value={h.link}>
            <input className="form-input--bare" value={h.link ?? ''} onChange={e => set({ link: e.target.value })} />
          </ReadOr>
        </Field>
      </div>
      <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>{t('fiche.p7.handover')}</button>
    </div>
  );
}

function LivePanel({ detail }: any) {
  const { t, lang } = useT();
  const { isCde, canSeeKpis } = useRole();
  const [rework, setRework] = useState<any | null>(null);

  const [reworkError,setReworkError]=useState('');
  const openRework = async () => {
    try {
    const created = await api.createProcess({ name: detail.name, kind: 'rework', reworks_process_id: detail.id });
    setRework(created);
    window.location.hash = `#/process/${created.id}`;
    } catch(e:any) {setReworkError(e.message);}
  };

  return (
    <div>
      <div className="detail-row"><span className="detail-row-label">{t('fiche.p8.since')}</span> {formatDate(detail.inventory_added_at ?? detail.updated_at, lang)}</div>
      <div className="detail-row"><span className="detail-row-label">{t('fiche.p1.owner')}</span> {detail.process_owner_name ?? '—'}</div>
      {canSeeKpis && detail.kpis.length > 0 && (
        <div className="detail-row"><span className="detail-row-label">{t('fiche.p4.kpis')}</span> {detail.kpis.map((k: any) => k.name).join(' · ')}</div>
      )}
      {reworkError && <p className="text-sm text-err" role="alert">{reworkError}</p>}
      {isCde && !rework && (
        <button className="btn btn-accent btn-sm" style={{ marginTop: 'var(--s12)' }} onClick={openRework}>
          {t('fiche.p8.openRework')}
        </button>
      )}
    </div>
  );
}

// ── Return modal (motif required) ────────────────────────────────────────────

function ReturnModal({ phases, onClose, onReturn }: {
  phases: any[];
  onClose: () => void;
  onReturn: (phaseKey: string, motif: string) => void;
}) {
  const { t, lang } = useT();
  const [target, setTarget] = useState(phases[phases.length - 1]?.key ?? '');
  const [motif, setMotif] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('fiche.returnTitle')}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {phases.length > 1 && (
            <div className="selectable-group" style={{ marginBottom: 'var(--s12)' }}>
              {phases.map(p => (
                <button key={p.key} type="button" className={`selectable${target === p.key ? ' active' : ''}`}
                        onClick={() => setTarget(p.key)}>
                  {p.sort_order} · {lang === 'fr' ? p.label_fr : p.label_en || p.label_fr}
                </button>
              ))}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('fiche.returnMotif')}</label>
            <textarea className="form-textarea" rows={3} autoFocus value={motif} onChange={e => setMotif(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-ghost" style={{ color: 'var(--err)' }} disabled={!motif.trim() || !target}
                  onClick={() => onReturn(target, motif.trim())}>
            {t('fiche.return')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pick / create a diagram file when none is linked ─────────────────────────

function PickFile({ processId, tier, area, onLinked }: any) {
  const { t } = useT();
  const [index, setIndex] = useState<any | null>(null);
  useEffect(() => { api.getRepoIndex().then(setIndex).catch(() => {}); }, []);
  const options = (index?.entries ?? []).filter((e: any) => !tier || e.tier === tier);

  return (
    <div className="flex gap-8 items-center">
      <select className="form-select" style={{ width: 'auto' }} value=""
              onChange={e => { if (e.target.value) api.linkRepoFile(processId, e.target.value).then(onLinked); }}>
        <option value="">{t('fiche.diagramPick')}</option>
        {options.map((f: any) => (
          <option key={f.path} value={f.path}>
            {f.title}{f.variant ? ` (${f.variant})` : ''}{f.isEmpty ? ' — vide' : ''}
          </option>
        ))}
      </select>
      {area && <span className="text-xs text-ink-3">{tier} / {area}</span>}
    </div>
  );
}

// ── The fiche of a documented file (no cycle record) ─────────────────────────

function FileFiche({ path }: { path: string }) {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const [entry, setEntry] = useState<any | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getRepoIndex()
      .then(idx => setEntry(idx.entries.find((e: any) => e.path === path) ?? null))
      .catch(e => setError(e.message));
    api.getRepoHistory(path).then(setHistory).catch(() => {});
  }, [path]);

  const adopt = async () => {
    try {
      const created = await api.adoptFile(path, entry?.title);
      window.location.hash = `#/process/${created.id}`;
    } catch (e: any) {
      if (e.data?.existing) window.location.hash = `#/process/${e.data.existing.id}`;
      else setError(e.message);
    }
  };

  return (
    <div className="fiche-page">
      <div className="fiche-head cde-form-header" style={{ marginBottom: 0 }}>
        <div className="fiche-head-main">
          <button className="form-back" style={{ marginBottom: 'var(--s8)' }} onClick={() => { window.location.hash = '#/repository'; }}>
            <ArrowLeft className="icon-sm" /> {t('common.back')}
          </button>
          <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
            <span className="bare-title" style={{ fontSize: 'var(--t-17)', fontWeight: 600 }}>{entry?.title ?? path.split('/').pop()}</span>
            {entry?.variant && <span className="variant-chip on" style={{ cursor: 'default' }}>{entry.variant}</span>}
            <span className="state-chip state-chip--documented">{t('rep.state.documented')}</span>
          </div>
          <div className="rep-path" style={{ marginTop: 2 }}>{path}</div>
        </div>
      </div>

      {error && <div className="text-err text-sm">{error}</div>}

      <div className="fiche-layout">
        <div className="fiche-left">
          <DiagramColumn path={path} lanes={entry?.lanes ?? []} canEditDiagram={false} />
        </div>
        <div className="fiche-rail-col">
          <div className="detail-section">
            <p className="text-sm text-ink-2">{t('fiche.noCycleTitle')}</p>
            {isCde && (
              <button className="btn btn-accent btn-sm" style={{ marginTop: 'var(--s8)' }} onClick={adopt}>
                {t('rep.openCycle')}
              </button>
            )}
          </div>
          <div className="detail-section">
            <div className="detail-label">{t('fiche.lastModified')}</div>
            <div className="text-sm">{entry?.lastCommit ? `${agoLabel(entry.lastCommit.date, lang)} · ${entry.lastCommit.author}` : '—'}</div>
          </div>
          {entry?.lanes?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('fiche.lanes')}</div>
              <div className="roles-chips">
                {entry.lanes.map((l: string) => <span key={l} className="ghost-chip">{l}</span>)}
              </div>
            </div>
          )}
          <div className="detail-section">
            <div className="detail-label">{t('fiche.commits')}</div>
            {history.length === 0 ? <div className="text-sm text-ink-3">—</div> : history.slice(0, 10).map(c => (
              <div key={c.sha} className="detail-row">
                <span className="detail-row-label mono" style={{ minWidth: 70 }}>{c.sha.slice(0, 8)}</span>
                <span className="text-sm">{c.message?.split('\n')[0]} <span className="text-ink-3">· {c.author}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
